
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./App.css";


const granularityMap = {
  "1m": 60,
  "5m": 300,
  "15m": 900,
  "1h": 3600,
  "6h": 21600,
  "1d": 86400,
};



const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;


const formatCandles = (raw) => {
    return raw.map(candle => ({
      time: candle[0],
      open: candle[3],
      high: candle[2],
      low: candle[1],
      close: candle[4]
    })).reverse();
  };



function isDuplicateTrade(prevLogs, coin, label, profitPercentage) {
  const now = Date.now();

  return prevLogs.some(note => {
    const isSameCoin = note.coin === coin;
    const isSameTimeframe = note.timeframe === label;
    const timestamp = note.timestamp || 0;
    const timeDiff = (now - timestamp) / 1000 / 60;

    const profitMatch = note.message.match(/Profit Potential: \+([\d.]+)%/);
    const previousProfit = profitMatch ? parseFloat(profitMatch[1]) : null;

    const isSimilarProfit = previousProfit !== null && Math.abs(previousProfit - profitPercentage) < 1.0;

    return isSameCoin && isSameTimeframe && isSimilarProfit;
  });
}

function shouldSendNewTrade(recentMap, coin, label, grade, cooldownMinutes = 60) {
  const key = `${coin}-${label}-${grade}`;
  const now = Date.now();
  const lastSent = recentMap[key];

  if (!lastSent) return true;

  const minutesAgo = (now - lastSent) / (1000 * 60);
  return minutesAgo >= cooldownMinutes;
}

function shouldSkipByUserMode(userMode, alertTag, label) {
  const conservativeTags = ["📊 High Potential Setup", "✅ Strong Trade Signal", "⚡ Breakout Candidate"];
  const moderateTags = ["🔰 Watchlist Candidate", ...conservativeTags];

  if (userMode === "Conservative") {
    // Only scan 1h, 6h, and 1d, and only allow top-tier tags
    const allowedTimeframes = ["1h", "6h", "1d"];
    return !(
      allowedTimeframes.includes(label) &&
      conservativeTags.includes(alertTag)
    );
  }

  if (userMode === "Moderate") {
    // Skip risky setups on longer timeframes
    if ((label === "1h" || label === "6h" || label === "1d") && alertTag === "🌗 Risky Setup") {
      return true;
    }
    return !moderateTags.includes(alertTag);
  }

  return false;
}
// ✅ You need to add this now
function isBelowGradeThreshold(userMode, confidenceScore) {
  if (userMode === "Conservative" && confidenceScore < 7) return true;
  if (userMode === "Moderate" && confidenceScore < 6) return true;
  if (userMode === "Aggressive" && confidenceScore < 4) return true;
  return false;
}
// Helper function to return the confirmation timeframes for a given label
function getConfirmationTimeframes(label) {
    // Define buckets:
    // Short-Term Bucket: 5m, 15m, 30m, 1h, 3hr
    // Mid-Term Bucket: 4h, 5hr, 8hr, 10hr
    // Day Trade Bucket: 12hr, 15hr, 18hr, 20hr, 1d
  
    const confirmationMap = {
      "1m": ["5m"],                 // 1m signal confirmed by 5m
     "5m": ["15m", "1h"],           // 5m signal confirmed by 15m and 1h
     "15m": ["1h", "6h"],           // 15m signal confirmed by 1h and 6h
     "1h": ["6h"],                // 1h signal confirmed by 6h
     "6h": [],                    // 6h stands alone
     "1d": []                     // 1d stands alone
   };

  
    return confirmationMap[label] || [];
  }

  const coins = [
    "BTC-USD",
    "ETH-USD",
    "SOL-USD",
    "XRP-USD",
    "LTC-USD",
    "ADA-USD",
    "DOGE-USD",
    "DOT-USD",
    "LINK-USD",
    "MPL-USD",
    "PRQ-USD",
    "POL-USD",
    "COMP-USD",
    "BLZ-USD",
    "DRIFT-USD",
    "MKR-USD",
    "AVAX-USD",
    "SUI-USD",
    "ONDO-USD",
    "FORT-USD",
    "L3-USD",
    "CRO-USD",
    "REQ-USD",
    "MATIC-USD",
    "API3-USD",
    "ORCA-USD",
    "EOS-USD",
    "INV-USD",
    "B3-USD",
    "XCN-USD",
    "AAVE-USD",
    "NEAR-USD",
    "RARE-USD",
    "ZRO-USD",
    "VARA-USD",
    "WIF-USD",
    "SEI-USD",
    "XLM-USD",
    "JASMY-USD",
    
    
];
function App() {
    const [livePrices, setLivePrices] = useState({});
    const [tradeSignal, setTradeSignal] = useState("");
    const [entryPrice, setEntryPrice] = useState(null);
    const [exitPrice, setExitPrice] = useState(null);
    const [selectedCoin, setSelectedCoin] = useState("BTC-USD");
    const [tradeAmount, setTradeAmount] = useState(100);
    const [tradeFee, setTradeFee] = useState(0.1);
    const [profitTargetLow, setProfitTargetLow] = useState(1.5);
    const [profitTargetHigh, setProfitTargetHigh] = useState(5);
    const [scanFrequency, setScanFrequency] = useState(15);
    const [potentialProfit, setPotentialProfit] = useState(0);
    const [followingTrade, setFollowingTrade] = useState(false);
    const [tradeLog, setTradeLog] = useState([]);
    const [aiGrade, setAiGrade] = useState(null);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [menuCollapsed, setMenuCollapsed] = useState(false);
    const [timeframes, setTimeframes] = useState({ "1m": true, "5m": true, "15m": true, "1h": true, "6h": true, "1d": true });
    const [tradeStyles, setTradeStyles] = useState({
      scalp: true,
      swing: true,
      day: true
    });
    const [notificationLog, setNotificationLog] = useState([]);
    const [newNotificationCount, setNewNotificationCount] = useState(0);
    const [followedTrades, setFollowedTrades] = useState([]);
    const [recentAlertsMap, setRecentAlertsMap] = useState({});
    const [selectedTimeframe, setSelectedTimeframe] = useState("1d"); // Default to 1-day chart
    const [aiInput, setAiInput] = useState("");
    const [userMode, setUserMode] = useState("Moderate");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [followedCoins, setFollowedCoins] = useState(() => {
             const defaults = {};
             coins.forEach(coin => { defaults[coin] = true; });
             return defaults;
          });
    const recentAlertsRef = useRef({});
    const [chartData, setChartData] = useState([]);
    const [selectedInterval, setSelectedInterval] = useState("1m");
    const [lastChartUpdate, setLastChartUpdate] = useState(Date.now());
    const handleIntervalChange = (interval) => {
      setSelectedInterval(interval);
    };
    const [gradeTimestampMap, setGradeTimestampMap] = useState({});
    


    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    
    useEffect(() => {
      if (activeTab === "alerts") {
        setNewNotificationCount(0);
      }
    }, [activeTab]);
const [aiMessages, setAiMessages] = useState([]);
const [aiLoading, setAiLoading] = useState(false);
const [dayChangeMap, setDayChangeMap] = useState({});
    let macdHistNow = 0;
    let macdHistPrev = 0;


    
    useEffect(() => {
      let intervalId;
 
      const fetchChartDataForInterval = async () => {
        try {
          const granularity = granularityMap[selectedInterval] || 3600;
          const response = await axios.get(
            `https://api.exchange.coinbase.com/products/${selectedCoin}/candles?granularity=${granularity}`
          );
          const formatted = formatCandles(response.data);
          console.log("📊 Chart updated at:", new Date().toLocaleTimeString());
          if (!chartData || chartData.length === 0) return;
          const lastTime = chartData[0]?.time;
          if (!lastTime || formatted[0].time !== lastTime) {
            setChartData(formatted);
            setLastChartUpdate(Date.now());
          }
        } catch (error) {
          console.error("Error fetching interval chart data:", error);
        }
      };
 
      fetchChartDataForInterval();
 
      // Refresh every 5 seconds ONLY for the selected chart
      intervalId = setInterval(() => {
        fetchChartDataForInterval();
      }, 5000);
 
      return () => clearInterval(intervalId);
    }, [selectedCoin, selectedInterval, chartData]);



const handleAiMessage = async () => {
    if (!aiInput.trim()) return;

    const userMessage = { sender: "user", text: aiInput };
    setAiMessages((prev) => [...prev, userMessage]);
    setAiInput("");
    setAiLoading(true);

    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-4",
          messages: [
            { role: "system", content: "You are a helpful crypto trading assistant. Answer clearly and concisely." },
            { role: "user", content: aiInput }
          ],
          temperature: 0.7
        })
      });

      const data = await response.json();
      setAiMessages((prev) => [...prev, { sender: "ai", text: data.choices[0].message.content }]);
    } catch (error) {
        console.error("AI Response Error:", error);
        setAiMessages((prev) => [...prev, { sender: "ai", text: "⚠️ AI is currently unavailable. Try again later." }]);
    } finally {
        setAiLoading(false);
    }
};


 
    const handleFollowTrade = (note) => {
    setFollowedTrades((prevTrades) => [...prevTrades, note.message]); // Store only message
    setFollowingTrade(true);
    setActiveTab('followed');
    // Remove the followed trade from notificationLog
    setNotificationLog(prev => prev.filter(notification => notification.message !== note.message));
    };

    useEffect(() => {
        const fetchDayChange = async () => {
          try {
            const changes = {};
            for (const coin of coins) {
                try {
                // Fetch the last 2 daily candles
                const dailyRes = await axios.get(
                  `https://api.exchange.coinbase.com/products/${coin}/candles?granularity=86400&limit=2`
                );
                const dailyCandles = dailyRes.data;
      
                // If we have at least 2 daily candles
                if (dailyCandles.length >= 2) {
                  // dailyCandles[0] => partial (today), dailyCandles[1] => fully closed (yesterday)
                  const yesterdayClose = dailyCandles[1][4];
                  const currentPrice = parseFloat(livePrices[coin] || 0);
      
                  // Compare yesterday's final close to today's live spot price
                  if (yesterdayClose > 0 && currentPrice > 0) {
                    const dayChange = ((currentPrice - yesterdayClose) / yesterdayClose) * 100;
                    changes[coin] = dayChange;
                  } else {
                    changes[coin] = 0;
                  }
                } else {
                  changes[coin] = 0;
                }
              } catch (err) {
                // If we get 404 or something else, set 0
                console.error(`Error fetching daily candles for ${coin}:`, err);
                changes[coin] = 0;
              }
            }
            setDayChangeMap(changes);
          } catch (error) {
            console.error("Error fetching 24h change data:", error);
          }
        };
      
        fetchDayChange();
        const interval = setInterval(fetchDayChange, 60_000);
        return () => clearInterval(interval);
      }, [coins, livePrices]);
      
     

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const updatedPrices = {};
                for (const coin of coins) {
                    const res = await axios.get(`https://api.coinbase.com/v2/prices/${coin}/spot`);
                    updatedPrices[coin] = parseFloat(res.data.data.amount).toFixed(4);
                }
                console.log("Updated Prices:", updatedPrices);
                setLivePrices(updatedPrices);
            } catch (error) {
                if (error.code === 'ERR_NETWORK_IO_SUSPENDED') {
                    console.warn("⚠️ Network I/O Suspended — Possible Chrome 3rd-party cookie restriction or browser policy interference.");
                }
                console.error("Error fetching live prices:", error);
            }
        };

        fetchPrices();
        const interval = setInterval(fetchPrices, 10000);
        return () => clearInterval(interval);
    }, []);
    const rsi = (values, period = 14) => {
      let gains = 0, losses = 0;
      for (let i = 1; i <= period; i++) {
          const change = values[i] - values[i - 1];
          if (change >= 0) gains += change; else losses -= change;
      }
      const avgGain = gains / period;
      const avgLoss = losses / period;
      const rs = avgGain / avgLoss;
      return 100 - 100 / (1 + rs);
  };

    const calculateIndicators = (candles) => {
      if (!candles || candles.length === 0) return {}; 
      const closePrices = candles.map(c => c[4]).reverse();

        const ema = (arr, period) => {
            const k = 2 / (period + 1);
            return arr.reduce((acc, val, i) => {
                if (i === 0) return [val];
                acc.push(val * k + acc[i - 1] * (1 - k));
                return acc;
            }, []);
        };

        
        const macdCalc = (data, short = 12, long = 26, signal = 9) => {
            const emaShort = ema(data, short);
            const emaLong = ema(data, long);
            const macdLine = emaShort.map((val, i) => val - emaLong[i]);
            const signalLine = ema(macdLine.slice(long - short), signal);
            return {
                macd: macdLine[macdLine.length - 1],
                signal: signalLine[signalLine.length - 1],
            };
        };

        const ema9 = ema(closePrices, 9).pop();
        const rsiValue = rsi(closePrices);
        const { macd, signal } = macdCalc(closePrices);

        const atr = (candles, period = 14) => {
            let trs = [];
            for (let i = 1; i < candles.length; i++) {
                const prevClose = candles[i - 1][4];
                const high = candles[i][2];
                const low = candles[i][3];
                const close = candles[i][4];
                const tr = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
                trs.push(tr);
            }
            const recentTRs = trs.slice(-period);
            return recentTRs.reduce((a, b) => a + b, 0) / recentTRs.length;
        };

        const atrValue = atr(candles);
        return { ema9, rsiValue, macd, signal, atrValue };
    };

    useEffect(() => {
        const fetchIndicators = async () => {
            try {
                let confirmFromHigherTimeframes = false;
                let atrValue = 0;
                for (const coin of coins) {
                    if (!followedCoins[coin]) {
                        continue;
                    }
                    
                    for (const [label, seconds] of Object.entries(granularityMap)) {
                        if (!timeframes[label]) continue;

                        const lowerTF = label === "15m" ? "5m" : label === "5m" ? "1m" : null;

if (lowerTF && timeframes[lowerTF]) {
  try {
    const lowerGranularity = granularityMap[lowerTF];
    const lowerRes = await axios.get(`https://api.exchange.coinbase.com/products/${coin}/candles?granularity=${lowerGranularity}`);
    const lowerCloses = lowerRes.data.map(c => c[4]).reverse();

    const lowerEma = (arr, period) => {
      const k = 2 / (period + 1);
      return arr.reduce((acc, val, i) => {
        if (i === 0) return [val];
        acc.push(val * k + acc[i - 1] * (1 - k));
        return acc;
      }, []);
    };

    const emaShort = lowerEma(lowerCloses, 12);
    const emaLong = lowerEma(lowerCloses, 26);
    const macdLine = emaShort.map((val, i) => val - emaLong[i]);
    const signalLine = lowerEma(macdLine.slice(emaShort.length - macdLine.length), 9);
    const hist = macdLine[macdLine.length - 1] - signalLine[signalLine.length - 1];

    const lowerRsiValue = rsi(lowerCloses);

    if (hist < 0 && lowerRsiValue < 55) {
      console.log(`⛔ Skipping ${coin} (${label}) — Lower TF (${lowerTF}) is bearish`);
      continue;
    }
  } catch (err) {
    console.warn(`⚠️ Failed lower TF check for ${coin} (${label}):`, err.message);
  }
}

                        const res = await axios.get(`https://api.exchange.coinbase.com/products/${coin}/candles?granularity=${seconds}`);
                    const candles = res.data;
                    const closePrices = candles.map(c => c[4]).reverse();
                    const { ema9, rsiValue, macd, signal } = calculateIndicators(candles);
                    atrValue = calculateIndicators(candles).atrValue;
                    // Duplicate declaration removed
                        const macdHistogramHistory = [];
                        
                        const emaShort = closePrices.reduce((acc, val, i) => {
                          const k = 2 / (12 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        
                        const emaLong = closePrices.reduce((acc, val, i) => {
                          const k = 2 / (26 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        
                        const macdLine = emaShort.map((val, i) => val - emaLong[i]);
                        const signalLine = macdLine.reduce((acc, val, i) => {
                          const k = 2 / (9 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        
                        for (let i = 0; i < signalLine.length; i++) {
                          macdHistogramHistory.push(macdLine[i] - signalLine[i]);
                        }
                        
                        macdHistNow = macdHistogramHistory[macdHistogramHistory.length - 1];
                        macdHistPrev = macdHistogramHistory[macdHistogramHistory.length - 2];
                        
                        const latestVolume = candles[0][5]; // Current candle volume
                        const priceRes = await axios.get(`https://api.coinbase.com/v2/prices/${coin}/spot`);
                        const coinPrice = parseFloat(priceRes.data.data.amount);
                        
                        const macdHistogram = macd - signal;
                        
                        const ema = (arr, period) => {
                            const k = 2 / (period + 1);
                            return arr.reduce((acc, val, i) => {
                                if (i === 0) return [val];
                                acc.push(val * k + acc[i - 1] * (1 - k));
                                return acc;
                            }, []);
                        };
                        
                       const emaHistory = ema(closePrices, 9);
                       const emaSlope = ema9 - emaHistory[emaHistory.length - 2];
                       
                       const volumeAvg = candles.slice(1, 11).reduce((sum, c) => sum + c[5], 0) / 10;
                       const volumeSurge = latestVolume > volumeAvg;
                       
                       const lastSwingHigh = Math.max(...candles.slice(-30).map(c => c[2]));
                       
                       // Resistance levels (placeholder logic)
                       const resistanceLevels = candles.slice(-50).map(c => c[2]); // high prices
                       resistanceLevels.sort((a, b) => b - a); // descending
                       const localResistances = [...new Set(resistanceLevels.filter(r => r > coinPrice))];
                       
                        let momentumScore = 0;
                        
                        if (macd > signal) momentumScore += 1;
                        if (macdHistogramHistory.length >= 3) {
                          const histCurrent = macdHistogramHistory[macdHistogramHistory.length - 1];
                          const histPrev1 = macdHistogramHistory[macdHistogramHistory.length - 2];
                          const histPrev2 = macdHistogramHistory[macdHistogramHistory.length - 3];
                          const momentumGrowing = histCurrent > histPrev1 && histPrev1 > histPrev2;
                        
                          if (momentumGrowing) {
                            momentumScore += 2;
                          }
                        }
                        const shortBreakout = coinPrice > Math.max(...closePrices.slice(0, 10));
const longBreakout = coinPrice > Math.max(...closePrices.slice(0, 40));
if (shortBreakout && longBreakout) momentumScore += 2;
else if (shortBreakout) momentumScore += 1;
                        if (latestVolume > volumeAvg) momentumScore += 1;
                        if (emaSlope > 0) momentumScore += 2;
                       
                       // 📈 Require RSI to be rising to award momentum point
const rsiHistory = closePrices.slice(-16).map((_, i, arr) => {
  if (i < 14) return null;
  const rsiVal = rsi(arr.slice(i - 14, i + 1));
  return rsiVal;
}).filter(Boolean);

if (rsiHistory.length >= 3) {
  const recentRSIs = rsiHistory.slice(-3);
  const rsiIsRising = recentRSIs[2] > recentRSIs[1] && recentRSIs[1] > recentRSIs[0];

  if (rsiIsRising && rsiValue >= 54 && rsiValue <= 70) {
    momentumScore += 1;
  }
}
                        if (rsiValue > 75) momentumScore -= 1;
                        if (confirmFromHigherTimeframes) momentumScore += 2;
                        if (volumeAvg < 100 && emaSlope < 0.01) momentumScore -= 1;
                        
                        const body = Math.abs(candles[0][4] - candles[0][1]);
                        const wick = candles[0][2] - candles[0][3];
                        const wickRatio = wick / (body || 1);
                        if (wickRatio > 3) momentumScore -= 1;
                        
                        const confidenceScore = momentumScore;
                       
                       const exitMultiplier =
                         confidenceScore === 10
                           ? 3
                           : confidenceScore >= 9
                           ? 2
                           : confidenceScore >= 6
                           ? 1.5
                           : 1;
                       
                       const adjustedExit = coinPrice + atrValue * exitMultiplier;
                       
                       const finalExit =
                         localResistances[0] && localResistances[0] < adjustedExit
                           ? localResistances[0]
                           : adjustedExit;
                       
                       const selectedExit = finalExit;
                       const realisticProfit = ((selectedExit - coinPrice) / coinPrice) * 100;
                       
                       const formatNumber = (num) => num.toLocaleString();
                        
                        // **Bucket-Based Multi-Timeframe Confirmation**
                      const confirmationTFs = getConfirmationTimeframes(label);



                        // **✅ AI trade conditions with balanced trend confirmation**
                        console.log(`Trade Analysis for ${coin} (${label}):
                            - Entry Price: $${coinPrice.toLocaleString()}
                            - Potential Exit: $${selectedExit.toLocaleString()}
                            - Last 50-Candle High: $${lastSwingHigh.toLocaleString()}
                            Higher Timeframe Confirmation: ${confirmFromHigherTimeframes}`);
                            // **✅ AI trade conditions with balanced trend confirmation**
                            console.log(`Trade Analysis for ${coin} (${label}):
    - Entry Price: $${coinPrice.toLocaleString()}
    - Potential Exit: $${selectedExit.toLocaleString()}
    - Last 50-Candle High: $${lastSwingHigh.toLocaleString()}
    - Higher Timeframe Confirmation: ${confirmFromHigherTimeframes}`);
                        
    if (label === "5m" || label === "15m") {
                                const body = Math.abs(candles[0][4] - candles[0][1]);
                                const wick = candles[0][2] - candles[0][3];
                                const wickRatio = wick / (body || 1); // prevent divide by zero

                                if (volumeAvg < 5000 && wickRatio > 3) {
                                  console.log(`⛔ Skipping ${coin} (${label}) — Illiquid or unstable`);
                                  continue;
                                }
                                else {
                                    console.log(`⛔ Skipped ${coin} (${label}) [${userMode.toUpperCase()} MODE]
                                  Reason: Confidence Score = ${confidenceScore}
                                  MACD = ${macd.toFixed(2)}, Histogram Now = ${macdHistNow.toFixed(2)}, Prev = ${macdHistPrev.toFixed(2)}
                                  Volume Surge = ${volumeSurge}, EMA Slope = ${emaSlope.toFixed(3)}
                                  RSI = ${rsiValue.toFixed(1)}, Profit = ${realisticProfit.toFixed(2)}%`);
                                  }
                                if (
                                    macd > signal &&
                                    rsiValue < 75 &&
                                    realisticProfit >= profitTargetLow && realisticProfit <= profitTargetHigh
                                ) {

                                    const tradeType = coinPrice > ema9 ? "📈 Short-Term Trade Opportunity" : "Quick Scalping Opportunity";
                                    const estimatedProfit = ((tradeAmount * realisticProfit) / 100) - ((tradeAmount * tradeFee) / 100);
                                    const profitPercentage = realisticProfit;
                                    
                                    const key = `${coin}-${label}`;
                                    const nowTimestamp = Date.now();
                                    const previous = recentAlertsRef.current[key];
                                    
                                    if (previous) {
                                      const timeDiffMinutes = (nowTimestamp - previous.timestamp) / (1000 * 60);
                                      const profitDiff = Math.abs(previous.profit - realisticProfit);
                                    
                                      if (profitDiff < 1.0 && timeDiffMinutes < 60) {
                                        console.log(`⏳ Skipping duplicate for ${coin} (${label}) - profit diff ${profitDiff.toFixed(2)}%, ${timeDiffMinutes.toFixed(1)} min apart`);
                                        continue;
                                      }
                                    }
                                    
                                    recentAlertsRef.current[key] = {
                                      timestamp: nowTimestamp,
                                      profit: realisticProfit
                                    };    
                                
                                    const now = new Date();
                                    const options = { timeZone: "America/Chicago", hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' };
                                    const centralTime = now.toLocaleString('en-US', options);
                                    
                      
                                    
                                    if (previous) {
                                      const timeDiffMinutes = (nowTimestamp - previous.timestamp) / (1000 * 60);
                                      const profitDiff = Math.abs(previous.profit - realisticProfit);
                                    
                                      if (profitDiff < 1.0 && timeDiffMinutes < 60) {
                                        console.log(`⏳ Skipping duplicate for ${coin} (${label}) - profit diff ${profitDiff.toFixed(2)}%, ${timeDiffMinutes.toFixed(1)} min apart`);
                                        continue;
                                      }
                                    }
                                    
                                    recentAlertsRef.current[key] = {
                                      timestamp: nowTimestamp,
                                      profit: realisticProfit
                                    };
                                    
                                    let alertTag = "";
                                    const isConservative = userMode === "Conservative";
                                    const isModerate = userMode === "Moderate";
                                    
if (confidenceScore >= 9 && macdHistNow > macdHistPrev && volumeSurge) {
  alertTag = "⚡ Breakout Candidate";
} else if (confidenceScore >= 7) {
  alertTag = "✅ Strong Trade Signal";
} else if (confidenceScore === 6) {
  alertTag = "📊 High Potential Setup";
} else if (confidenceScore === 5) {
  alertTag = "🔰 Watchlist Candidate";
} else if (confidenceScore >= 3) {
  alertTag = "🌗 Risky Setup";
} else {
  // Score is too low, skip this trade
  console.log(`❌ Skipping ${coin} (${label}) — Confidence score ${confidenceScore} too low`);
  continue;
}
                                    if (
                                      userMode === "Conservative" &&
                                      (label === "5m" || label === "15m" || label === "1h" || label === "6h" || label === "1d") &&
                                      alertTag !== "📊 High Potential Setup" &&
                                      alertTag !== "✅ Strong Trade Signal" &&
                                      alertTag !== "⚡ Breakout Candidate"
                                    ) {
                                      console.log(`⏳ Skipping ${coin} (${label}) in Conservative mode due to low alertTag: ${alertTag}`);
                                      continue;
                                    }
                                    if (shouldSkipByUserMode(userMode, alertTag, label)) {
                                      console.log(`⏳ Skipping ${coin} (${label}) due to ${userMode} mode and tag '${alertTag}'`);
                                      continue;
                                    }
                                    if (isDuplicateTrade(notificationLog, coin, label, realisticProfit)) {
                                      console.log(`⛔ Skipped duplicate trade for ${coin} (${label}) — same profit within 1%`);
                                      continue;
                                    }
                                    const message = `${alertTag}! ${coin} (${label} timeframe):
Entry: $${formatNumber(coinPrice.toFixed(4))}
Exit: $${formatNumber(selectedExit.toFixed(4))}
Profit Potential: +${profitPercentage.toFixed(2)}%
Estimated Profit: $${estimatedProfit.toFixed(2)}
Confidence Score: ${confidenceScore}/10
                              Reason: ${[
  macd > signal ? "MACD bullish" : null,
  macdHistNow > macdHistPrev ? "Momentum growing" : null,
  emaSlope > 0 ? "EMA rising" : null,
  latestVolume > volumeAvg * 1.2 ? "Volume surge" : null,
  rsiValue > 45 && rsiValue < 70 ? "Healthy RSI" : null,
  confirmFromHigherTimeframes ? "Higher TF agrees" : null
].filter(Boolean).join(", ")}
Timestamp: ${centralTime}`;
const gradeKey = `${coin}-${label}-${confidenceScore}`;
if (!shouldSendNewTrade(gradeTimestampMap, coin, label, confidenceScore)) {
  console.log(`⏳ Skipping ${coin} (${label}) Grade ${confidenceScore}/10 — already sent in last hour`);
} else {
  setGradeTimestampMap((prev) => ({ ...prev, [gradeKey]: Date.now() }));
  new Notification("Trade Alert", { body: message });
}
                                setNotificationLog((prev) => {
                                    const exists = prev.some(note => {
                                      if (note.coin !== coin || note.timeframe !== label) return false;
                                      const existingProfitMatch = note.message.match(/Profit Potential: \+([\d.]+)%/);
                                      if (!existingProfitMatch) return false;
                                      const existingProfit = parseFloat(existingProfitMatch[1]);
                                      return Math.abs(existingProfit - profitPercentage) < 1.0;
                                    });
                                    if (!exists) {
                                      if (activeTab !== "alerts") {
                                        setNewNotificationCount((prevCount) => prevCount + 1);
                                      }
                                      return [{ message, coin, timeframe: label, type: "trade", timestamp: Date.now() }, ...prev];
                                    }
                                    return prev;
                                  });
                                setTradeLog(prev => [...prev, { coin, entry: coinPrice, exit: selectedExit, result: "pending", timestamp: Date.now() }]);
                            }
                        } 
                              
                         {
                            const body = Math.abs(candles[0][4] - candles[0][1]);
                            const wick = candles[0][2] - candles[0][3];
                            const wickRatio = wick / (body || 1); // prevent divide by zero

                            if (volumeAvg < 5000 && wickRatio > 3) {
                              console.log(`⛔ Skipping ${coin} (${label}) — Illiquid or unstable`);
                              continue;
                            }
                            else {
                                console.log(`⛔ Skipped ${coin} (${label}) [${userMode.toUpperCase()} MODE]
                              Reason: Confidence Score = ${confidenceScore}
                              MACD = ${macd.toFixed(2)}, Histogram Now = ${macdHistNow.toFixed(2)}, Prev = ${macdHistPrev.toFixed(2)}
                              Volume Surge = ${volumeSurge}, EMA Slope = ${emaSlope.toFixed(3)}
                              RSI = ${rsiValue.toFixed(1)}, Profit = ${realisticProfit.toFixed(2)}%`);
                              }
                            if (
                                macd > signal &&
                                rsiValue < 70 &&
                                coinPrice > ema9 &&
                                realisticProfit >= profitTargetLow && realisticProfit <= profitTargetHigh
                            ) {
                                const tradeType = "✅ Strong Trade Signal";
                                const estimatedProfit = ((tradeAmount * realisticProfit) / 100) - ((tradeAmount * tradeFee) / 100);
                                const profitPercentage = realisticProfit;
                                const key = `${coin}-${label}`;
const nowTimestamp = Date.now();
const previous = recentAlertsRef.current[key];

if (previous) {
  const timeDiffMinutes = (nowTimestamp - previous.timestamp) / (1000 * 60);
  const profitDiff = Math.abs(previous.profit - realisticProfit);

  if (profitDiff < 1.0 && timeDiffMinutes < 60) {
    console.log(`⏳ Skipping duplicate for ${coin} (${label}) - profit diff ${profitDiff.toFixed(2)}%, ${timeDiffMinutes.toFixed(1)} min apart`);
    continue;
  }
}

recentAlertsRef.current[key] = {
  timestamp: nowTimestamp,
  profit: realisticProfit
};
                                const now = new Date();
                                const options = { timeZone: "America/Chicago", hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' };
                                const centralTime = now.toLocaleString('en-US', options);
                                
                                let alertTag = "";
// 🔒 Grade filter based on mode
if (isBelowGradeThreshold(userMode, confidenceScore)) {
  console.log(`❌ Skipping ${coin} (${label}) due to low grade ${confidenceScore}/10 for ${userMode} mode`);
  continue;
}

    if (confidenceScore >= 9 && macdHistNow > macdHistPrev && volumeSurge) {
      alertTag = "⚡ Breakout Candidate";
    } else if (confidenceScore >= 7) {
      alertTag = "✅ Strong Trade Signal";
    } else if (confidenceScore === 6) {
      alertTag = "📊 High Potential Setup";
    } else if (confidenceScore === 5) {
      alertTag = "🔰 Watchlist Candidate";
    } else if (confidenceScore == 4) {
      alertTag = "🌗 Risky Setup";
    } else {
      // Score is too low, skip this trade
      console.log(`❌ Skipping ${coin} (${label}) — Confidence score ${confidenceScore} too low`);
      continue;
    }
                                        if (isDuplicateTrade(notificationLog, coin, label, realisticProfit)) {
                                            console.log(`⛔ Skipped duplicate trade for ${coin} (${label}) — same profit within 1%`);
                                            continue;
                                          }
// const riskRewardRaw = (selectedExit - coinPrice) / Math.max(atrValue * 1.5, 0.0001);
// const riskRewardEmoji = riskRewardRaw >= 2 ? "💚" : riskRewardRaw >= 1 ? "🟡" : "🔴";
// const formattedRR = `${riskRewardEmoji} ${riskRewardRaw.toFixed(1)} to 1`;
    const message = `${alertTag}! ${coin} (${label} timeframe):
Entry: $${formatNumber(coinPrice.toFixed(4))}
Exit: $${formatNumber(selectedExit.toFixed(4))}
Profit Potential: +${profitPercentage.toFixed(2)}%
Estimated Profit: $${estimatedProfit.toFixed(2)}
AI Grade: ${confidenceScore}/10
Timestamp: ${centralTime}`;
const gradeKey = `${coin}-${label}-${confidenceScore}`;
if (!shouldSendNewTrade(gradeTimestampMap, coin, label, confidenceScore)) {
  console.log(`⏳ Skipping ${coin} (${label}) Grade ${confidenceScore}/10 — already sent in last hour`);
} else {
  setGradeTimestampMap((prev) => ({ ...prev, [gradeKey]: Date.now() }));
  new Notification("Trade Alert", { body: message });
}
                                setNotificationLog((prev) => {
                                    const exists = prev.some(note => 
                                      note.coin === coin && 
                                      note.timeframe === label && 
                                      note.message.includes(`Entry: $${coinPrice.toFixed(2)}`) &&
                                      note.message.includes(`Exit: $${selectedExit.toFixed(2)}`)
                                    );
                                    if (!exists) {
                                      if (activeTab !== "alerts") {
                                        setNewNotificationCount((prevCount) => prevCount + 1);
                                      }
                                      return [{ message, coin, timeframe: label, type: "trade", timestamp: Date.now() }, ...prev];
                                    }
                                    return prev;
                                  });
                                  setTradeLog(prev => [...prev, { coin, entry: coinPrice, exit: selectedExit, result: "pending", timestamp: Date.now() }]);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching indicators:", error);
            }
        };
    
        fetchIndicators();
        const interval = setInterval(fetchIndicators, scanFrequency * 1000);
        return () => clearInterval(interval);
    }, [scanFrequency, profitTargetLow, profitTargetHigh, tradeAmount, tradeFee, timeframes]);
    
    useEffect(() => {
        const price = parseFloat(livePrices[selectedCoin]);
      
        if (followingTrade && entryPrice && price) {
          const bearishThreshold = entryPrice * 0.99;
      
          // 🔁 Find the matching trade to monitor
          const trade = tradeLog.find(
            (log) =>
              log.coin === selectedCoin &&
              log.entry === entryPrice &&
              log.result === "pending"
          );
      
          if (trade) {
            const priceDifference = price - entryPrice;
          const tradeDurationMinutes = (Date.now() - (trade.timestamp || Date.now())) / 60000;
          
          // ⏱ Time-in-Trade Monitoring
          if (tradeDurationMinutes >= 10 && macdHistNow < macdHistPrev && price < exitPrice && price < entryPrice * 1.01) {
            const stallMsg = `⏱ ${selectedCoin} (${selectedTimeframe}) has stalled for over 10 minutes. Momentum fading — consider exiting early.`;
            new Notification("Trade Timeout", { body: stallMsg });
          
            setNotificationLog((prev) => [
              {
                message: stallMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "early-exit-warning",
              },
              ...prev,
            ]);
          }
      
            // 🚀 Momentum picking up — raise the exit target slightly
            if (price > exitPrice && priceDifference > 0.005 * entryPrice) {
              const newExit = price + 0.003 * entryPrice;
              const adjustedMsg = `📈 Momentum Update: ${selectedCoin} (${selectedTimeframe}) is gaining strength. New exit target: $${newExit.toFixed(2)}.`;
              new Notification("Exit Adjusted", { body: adjustedMsg });
      
              setNotificationLog((prev) => [
                {
                  message: adjustedMsg,
                  coin: selectedCoin,
                  timeframe: selectedTimeframe,
                  type: "adjusted",
                },
                ...prev,
              ]);
              setExitPrice(newExit);
            }
      
            // ⚠️ Weak momentum — suggest lowering expectations
            else if (price < exitPrice && price < entryPrice * 1.005) {
              const earlyExitMsg = `⚠️ Weakening Momentum: ${selectedCoin} (${selectedTimeframe}). Consider reducing your target or exiting early.`;
              new Notification("Exit Warning", { body: earlyExitMsg });
      
              setNotificationLog((prev) => [
                {
                  message: earlyExitMsg,
                  coin: selectedCoin,
                  timeframe: selectedTimeframe,
                  type: "early-exit-warning",
                },
                ...prev,
              ]);
            }
          }
      
          // ✅ Success Exit
          if (price >= exitPrice) {
            const successMsg = `🎉 Trade Successful! ${selectedCoin} (${selectedTimeframe}): Enter at $${entryPrice.toFixed(2)}, Exit at $${exitPrice.toFixed(2)}`;
            new Notification("Trade Success", { body: successMsg });
      
            setNotificationLog((prev) => [
              {
                message: successMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "success",
              },
              ...prev,
            ]);
      
            setTradeLog((prev) =>
              prev.map((log) =>
                log.entry === entryPrice ? { ...log, result: "success" } : log
              )
            );
      
            setFollowingTrade(false);
          }
      
          // ❌ Bearish Exit
          else if (price <= bearishThreshold) {
            const bearishMsg = `⚠️ Bearish Alert! ${selectedCoin} (${selectedTimeframe}): Entry at $${entryPrice.toFixed(
              2
            )}, Current Price: $${price.toFixed(2)}. Consider exiting.`;
            new Notification("Trade Alert", { body: bearishMsg });
      
            setNotificationLog((prev) => [
              {
                message: bearishMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "bearish",
              },
              ...prev,
            ]);
      
            setTradeLog((prev) =>
              prev.map((log) =>
                log.entry === entryPrice ? { ...log, result: "bearish" } : log
              )
            );
      
            setFollowingTrade(false);
          }
        }
      }, [livePrices, followingTrade, entryPrice, exitPrice, selectedCoin, selectedTimeframe, tradeLog]);
    
    const handleTimeframeToggle = (label) => {
        setTimeframes(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const filteredLogs = activeTab === "followed" ? followedTrades : tradeLog.filter(log => log.result !== "pending");
    
    // if (isMobile) {
//   return (
//     <div style={{ padding: 20, color: "#ccc", backgroundColor: "#0d1117", minHeight: "100vh" }}>
//       <h2>📵 Desktop-Only Version</h2>
//       <p>This version of the Crypto Trade Advisor is optimized for desktop. Please visit this app on a larger screen for the best experience.</p>
//     </div>
//   );
// }
    
    return (
        <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "#0d1117", color: "#8b949e", fontFamily: "Arial" }}>
        <aside style={{ width: menuCollapsed ? "20px" : "220px", backgroundColor: "#161b22", padding: "20px", transition: "width 0.3s" }}>
            <button onClick={() => setMenuCollapsed(!menuCollapsed)} style={{ backgroundColor: "transparent", color: "#58a6ff", border: "none", fontSize: "1.5rem", marginBottom: "20px" }}>
                {menuCollapsed ? "☰" : "✖"}
            </button>
            {!menuCollapsed && (
                <>
                    <h2 style={{ color: "#58a6ff" }}>Menu</h2>
                   {/* Sidebar Menu */}
                   <button 
onClick={() => setActiveTab("quick_view")} 
style={{ 
display: "block", 
width: "100%", 
textAlign: "left",
padding: "12px 15px",
marginBottom: "10px",
fontSize: "1rem",
fontWeight: "bold",
borderRadius: "8px",  
border: "none",
cursor: "pointer",
transition: "all 0.3s ease-in-out",
background: activeTab === "quick_view" ? "#2563eb" : "none",
color: activeTab === "quick_view" ? "#fff" : "white",
boxShadow: activeTab === "quick_view" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none",
position: "relative"
}}
onMouseEnter={(e) => e.target.style.background = activeTab === "quick_view" ? "#1E50A2" : "#2C2F33"}
onMouseLeave={(e) => e.target.style.background = activeTab === "quick_view" ? "#2563eb" : "none"}
>
    📈 Quick View
</button>


<button 
     
    onClick={() => setActiveTab("dashboard")} 
    style={{ 
        display: "block", 
        width: "100%", 
        textAlign: "left",
        padding: "12px 15px",
        marginBottom: "10px",
        fontSize: "1rem",
        fontWeight: "bold",
        borderRadius: "8px",  
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        background: activeTab === "dashboard" ? "#2563eb" : "none", // ✅ Updated to blue
        color: activeTab === "dashboard" ? "#fff" : "white",
        boxShadow: activeTab === "dashboard" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none", // ✅ Updated shadow
    }}
    onMouseEnter={(e) => e.target.style.background = activeTab === "dashboard" ? "#1E50A2" : "#2C2F33"} // ✅ Updated hover effect
    onMouseLeave={(e) => e.target.style.background = activeTab === "dashboard" ? "#2563eb" : "none"}
>
    📝 Dashboard
</button>

<button 
     onClick={() => setActiveTab("followed")} 
     style={{ 
         display: "block", 
         width: "100%", 
         textAlign: "left",
         padding: "12px 15px",
         marginBottom: "10px",
         fontSize: "1rem",
         fontWeight: "bold",
         borderRadius: "8px",  
         border: "none",
         cursor: "pointer",
         transition: "all 0.3s ease-in-out",
         background: activeTab === "followed" ? "#2563eb" : "none", // ✅ Updated to blue
         color: activeTab === "followed" ? "#fff" : "white",
         boxShadow: activeTab === "followed" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none", // ✅ Updated shadow
     }}
     onMouseEnter={(e) => e.target.style.background = activeTab === "followed" ? "#1E50A2" : "#2C2F33"} // ✅ Updated hover effect
     onMouseLeave={(e) => e.target.style.background = activeTab === "followed" ? "#2563eb" : "none"}
 >
    📍 Followed Trades
</button>

<button 
     onClick={() => setActiveTab("history")} 
     style={{ 
         display: "block", 
         width: "100%", 
         textAlign: "left",
         padding: "12px 15px",
         marginBottom: "10px",
         fontSize: "1rem",
         fontWeight: "bold",
         borderRadius: "8px",  
         border: "none",
         cursor: "pointer",
         transition: "all 0.3s ease-in-out",
         background: activeTab === "history" ? "#2563eb" : "none", // ✅ Updated to blue
         color: activeTab === "history" ? "#fff" : "white",
         boxShadow: activeTab === "history" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none", // ✅ Updated shadow
     }}
     onMouseEnter={(e) => e.target.style.background = activeTab === "history" ? "#1E50A2" : "#2C2F33"} // ✅ Updated hover effect
     onMouseLeave={(e) => e.target.style.background = activeTab === "history" ? "#2563eb" : "none"}
 >
    📊 Trade History
</button>

<button 
     onClick={() => setActiveTab("alerts")} 
     style={{ 
         display: "block", 
         width: "100%", 
         textAlign: "left",
         padding: "12px 15px",
         marginBottom: "10px",
         fontSize: "1rem",
         fontWeight: "bold",
         borderRadius: "8px",  
         border: "none",
         cursor: "pointer",
         transition: "all 0.3s ease-in-out",
         background: activeTab === "alerts" ? "#2563eb" : "none", // ✅ Updated to blue
         color: activeTab === "alerts" ? "#fff" : "white",
         boxShadow: activeTab === "alerts" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none", // ✅ Updated shadow
     }}
     onMouseEnter={(e) => e.target.style.background = activeTab === "alerts" ? "#1E50A2" : "#2C2F33"} // ✅ Updated hover effect
     onMouseLeave={(e) => e.target.style.background = activeTab === "alerts" ? "#2563eb" : "none"}
 >
    🔔 Notifications
    {newNotificationCount > 0 && (
      <span style={{
        marginLeft: "8px",
        backgroundColor: "#ff4d4d",
        color: "white",
        borderRadius: "12px",
        padding: "2px 8px",
        fontSize: "12px",
        fontWeight: "bold"
      }}>
        {newNotificationCount}
      </span>
    )}
</button>

<button 
    onClick={() => setActiveTab("ai_advisor")} 
    style={{ 
        display: "block", 
        width: "100%", 
        textAlign: "left",
        padding: "12px 15px",
        marginBottom: "10px",
        fontSize: "1rem",
        fontWeight: "bold",
        borderRadius: "8px",  
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        background: activeTab === "ai_advisor" ? "#2563eb" : "none", // ✅ Updated to blue
        color: activeTab === "ai_advisor" ? "#fff" : "white",
        boxShadow: activeTab === "ai_advisor" ? "0px 4px 10px rgba(37, 99, 235, 0.6)" : "none", // ✅ Updated shadow
    }}
    onMouseEnter={(e) => e.target.style.background = activeTab === "ai_advisor" ? "#1E50A2" : "#2C2F33"} // ✅ Updated hover effect
    onMouseLeave={(e) => e.target.style.background = activeTab === "ai_advisor" ? "#2563eb" : "none"}
>
    💰 AI Trade Advisor
</button>

                    </>
                )}
            </aside>

            <main className="container">
    <h1 style={{ color: "#58a6ff" }}>Crypto Trade Advisor</h1>
    <h2>
      {selectedCoin} Price: {livePrices[selectedCoin] ? `$${livePrices[selectedCoin]}` : "Loading..."}
      <span style={{ fontSize: "14px", marginLeft: "10px", color: "#888" }}>
        (Updated: {new Date().toLocaleTimeString()})
      </span>
    </h2>

    {activeTab === "ai_advisor" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "10px", border: "1px solid #333", height: "70vh", overflow: "auto" }}>
    <h3 style={{ color: "#58a6ff" }}>💬 GPT Assistant</h3>
    <div style={{ maxHeight: "60%", overflowY: "auto", marginBottom: "10px", padding: "10px", backgroundColor: "#0d1117", borderRadius: "8px", border: "1px solid #333" }}>
      {aiMessages.map((msg, index) => (
        <div key={index} style={{ marginBottom: "10px", textAlign: msg.sender === "user" ? "right" : "left" }}>
          <div style={{ 
            display: "inline-block",
            padding: "10px",
            backgroundColor: msg.sender === "user" ? "#2563eb" : "#2c2f33",
            color: "#fff",
            borderRadius: "10px",
            maxWidth: "80%"
          }}>
            {msg.text}
          </div>
        </div>
      ))}
    </div>
    <div style={{ display: "flex", gap: "10px" }}>
      <input
        type="text"
        value={aiInput}
        onChange={(e) => setAiInput(e.target.value)}
        placeholder="Ask your trading assistant anything..."
        style={{
          flexGrow: 1,
          padding: "10px",
          borderRadius: "8px",
          border: "1px solid #333",
          backgroundColor: "#1f2937",
          color: "#fff"
        }}
      />
      <button
        onClick={handleAiMessage}
        disabled={aiLoading}
        style={{
          padding: "10px 20px",
          borderRadius: "8px",
          backgroundColor: "#2563eb",
          color: "white",
          border: "none",
          cursor: aiLoading ? "not-allowed" : "pointer"
        }}
      >
        {aiLoading ? "..." : "Send"}
      </button>
    </div>
  </div>
)}       

{activeTab === "alerts" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "10px", border: "1px solid #333" }}>
    <h3 style={{ color: "#58a6ff" }}>🔔 Notification History</h3>
    
    {notificationLog.length === 0 ? (
      <p style={{ color: "#ccc" }}>No alerts yet.</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {notificationLog.map((note, i) => (
          <div
            key={i}
            style={{
              backgroundColor: "#161b22",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #2c2f33",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: "10px", fontWeight: "bold", color: "#58a6ff" }}>
                {i + 1}.
              </span>
              <div>
                <p
                  onClick={() => {
                    setSelectedCoin(note.coin);
                    setActiveTab("dashboard");
                  }}
                  style={{ cursor: "pointer", color: "#58a6ff" }}
                >
                  {note.message}
                </p>
                <p style={{ fontSize: "12px", color: "#aaa" }}>
                  Coin: {note.coin} | Timeframe: {note.timeframe} | Type: {note.type}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button
                onClick={() => setNotificationLog(prev => prev.filter((_, index) => index !== i))}
                style={{
                  backgroundColor: "#ff4d4d",
                  color: "white",
                  border: "none",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                ❌ Remove
              </button>
              <button
                onClick={() => handleFollowTrade(note)}
                style={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                Follow Trade
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

             
             {activeTab === "followed" && (
  <div
    style={{
      marginTop: "20px",
      padding: "20px",
      backgroundColor: "#1a1a1a",
      borderRadius: "10px",
      border: "1px solid #333"
    }}
  >
    <h3 style={{ color: "#58a6ff" }}>📍 Followed Trades</h3>
    {followedTrades.length === 0 ? (
      <p style={{ color: "#ccc" }}>No followed trades yet.</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {followedTrades.map((trade, i) => {
          // Optional: parse the coin from the trade string
          const coinRegex = /([A-Z]+-USD)/;
          const match = trade.match(coinRegex);
          const parsedCoin = match ? match[1] : null;

          return (
            <div
              key={i}
              onClick={() => {
                // If we found a coin symbol in the string, set it
                if (parsedCoin) {
                  setSelectedCoin(parsedCoin);
                }
                // Switch to the dashboard
                setActiveTab("dashboard");
              }}
              style={{
                backgroundColor: "#2c2f33",
                padding: "10px",
                borderRadius: "8px",
                marginBottom: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer"
              }}
            >
              <p style={{ margin: 0, color: "#8b949e" }}>{trade}</p>
              <button
                onClick={(e) => {
                  // Prevent the parent div's onClick from firing
                  e.stopPropagation();
                  setFollowedTrades((prev) =>
                    prev.filter((_, index) => index !== i)
                  );
                }}
                style={{
                  backgroundColor: "#ff4d4d",
                  color: "white",
                  border: "none",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                ❌ Unfollow
              </button>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}
              
              {activeTab === "history" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "10px", border: "1px solid #333" }}>
    <h3 style={{ color: "#58a6ff" }}>📊 Trade History</h3>
    {tradeLog.filter(log => log.result !== "pending").length === 0 ? (
      <p style={{ color: "#ccc" }}>No trades followed yet.</p>
    ) : (
      <>
        {(() => {
          const completedTrades = tradeLog.filter(log => log.result !== "pending");
          const successTrades = completedTrades.filter(log => log.result === "success");
          const successRate = ((successTrades.length / completedTrades.length) * 100).toFixed(2);
          return <p style={{ color: "#ccc" }}>Success Rate: {successRate}%</p>;
        })()}
        {tradeLog.filter(log => log.result !== "pending").map((log, i) => (
          <div key={i} style={{
            backgroundColor: "#2c2f33",
            padding: "10px",
            borderRadius: "8px",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center"
          }}>
            <div style={{ marginRight: "10px", fontWeight: "bold", color: "#58a6ff" }}>{i + 1}.</div>
            <div>
              <p style={{ margin: 0, color: "#8b949e" }}>
                {log.result === "success" ? "✅ Successful Trade" : log.result === "bearish" ? "⚠️ Bearish Exit" : "Trade"}
              </p>
              <p style={{ margin: 0, color: "#8b949e" }}>
                {log.coin} | Entry: ${log.entry.toFixed(2)} | Exit: ${log.exit.toFixed(2)}
              </p>
            </div>
          </div>
        ))}
      </>
    )}
  </div>
)}
 
 {activeTab === "quick_view" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "10px", border: "1px solid #333" }}>
    <h3 style={{ color: "#58a6ff" }}>📈 Quick View</h3>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #333", color: "#ccc" }}>
          <th style={{ textAlign: "left", padding: "8px" }}>Coin</th>
          <th style={{ textAlign: "left", padding: "8px" }}>Price (USD)</th>
          <th style={{ textAlign: "left", padding: "8px" }}>24h Change</th>
          <th style={{ textAlign: "left", padding: "8px" }}>Follow</th>
        </tr>
      </thead>
      <tbody>
        {coins.map((coin, index) => {
          const currentPrice = livePrices[coin] || "Loading";
          const dayChange = dayChangeMap[coin] ?? 0;  // default to 0 if undefined
          const dayChangeColor = dayChange >= 0 ? "#3ba55d" : "#ff4d4d";  // green or red

          return (
            <tr
              key={coin}
              onClick={(e) => {
                if (e.target.closest("label")) return;
                setSelectedCoin(coin);
                setActiveTab("dashboard");
              }}
              style={{
                backgroundColor: index % 2 === 0 ? "#1a1a1a" : "#202020",
                borderBottom: "1px solid #2c2f33",
                cursor: "pointer"
              }}
            >
              <td style={{ padding: "8px" }}>{coin}</td>
              <td style={{ padding: "8px" }}>${currentPrice}</td>
              <td style={{ padding: "8px", color: dayChangeColor }}>
                {dayChange.toFixed(2)}%
              </td>
              <td style={{ padding: "8px" }}>
                <label style={{ position: "relative", display: "inline-block", width: "40px", height: "20px" }}>
                  <input
                    type="checkbox"
                    checked={!!followedCoins[coin]}
                    onChange={() =>
                      setFollowedCoins(prev => ({ ...prev, [coin]: !prev[coin] }))
                    }
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: "absolute",
                    cursor: "pointer",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: followedCoins[coin] ? "#2563eb" : "#555",
                    borderRadius: "20px",
                    transition: ".4s"
                  }}>
                    <span style={{
                      position: "absolute",
                      height: "14px",
                      width: "14px",
                      left: followedCoins[coin] ? "22px" : "4px",
                      bottom: "3px",
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      transition: ".4s"
                    }} />
                  </span>
                </label>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
)}
              {activeTab === "dashboard" && (
                    <>
                        <div style={{ display: "flex", gap: "10px", marginBottom: "4px", flexWrap: "wrap" }}>
                            <div>
                                <label>Select Coin</label><br />
                                <select value={selectedCoin} onChange={(e) => setSelectedCoin(e.target.value)} style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "6px", border: "1px solid #333", padding: "6px" }}>
                                    {coins.map((coin) => (
                                        <option key={coin} value={coin}>{coin}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Min Profit %</label><br />
                                <input type="number" step="1" value={profitTargetLow} onChange={(e) => setProfitTargetLow(parseFloat(e.target.value))} style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "6px", border: "1px solid #333", padding: "6px" }} />
                            </div>
                            <div>
                                <label>Max Profit %</label><br />
                                <input type="number" step="1" value={profitTargetHigh} onChange={(e) => setProfitTargetHigh(parseFloat(e.target.value))} style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "6px", border: "1px solid #333", padding: "6px" }} />
                            </div>
                            <div>
                                <label>Amount ($)</label><br />
                                <input type="number" value={tradeAmount} onChange={(e) => setTradeAmount(parseFloat(e.target.value))} style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "6px", border: "1px solid #333", padding: "6px" }} />
                            </div>
                            <div>
                                <label>Fees (%)</label><br />
                                <input type="number" step="0.1" value={tradeFee} onChange={(e) => setTradeFee(parseFloat(e.target.value))} style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "6px", border: "1px solid #333", padding: "6px" }} />
                            </div>
                            <div>
                                <label>Trader Mode</label><br />
                                <select
                                    value={userMode}
                                    onChange={(e) => setUserMode(e.target.value)}
                                    style={{
                                        backgroundColor: "#1f2937",
                                        color: "white",
                                        borderRadius: "6px",
                                        border: "1px solid #333",
                                        padding: "6px"
                                    }}
                                >
                                    <option value="Conservative">Conservative</option>
                                    <option value="Moderate">Moderate</option>
                                    <option value="Aggressive">Aggressive</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ marginTop: "2px", padding: "10px" }}>
                          <h4 style={{ color: "#58a6ff" }}>Timeframe Selection</h4>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                            {["1m", "5m", "15m", "1h", "6h", "1d"].map((tf) => (
                              <label key={tf} style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#2c2f33", padding: "8px 12px", borderRadius: "8px", border: "1px solid #444" }}>
                                <input
                                  type="checkbox"
                                  checked={timeframes[tf]}
                                  onChange={() => setTimeframes((prev) => ({ ...prev, [tf]: !prev[tf] }))}
                                  style={{ transform: "scale(1.2)" }}
                                />
                                <span style={{ color: "#ccc" }}>{tf}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: "30px", marginTop: "10px", flexWrap: "wrap" }}>
  {[
    { label: "Scalp", key: "scalp", description: "5m & 15m" },
    { label: "Swing", key: "swing", description: "1h & 6h" },
    { label: "Day Trade", key: "day", description: "1d" }
  ].map(({ label, key, description }) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: "12px", background: "#1f2937", padding: "12px 16px", borderRadius: "10px", border: "1px solid #333" }}>
      <div>
        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#58a6ff" }}>{label}</div>
        <div style={{ fontSize: "14px", color: "#ccc" }}>{description}</div>
      </div>
      <label style={{ position: "relative", display: "inline-block", width: "50px", height: "26px" }}>
        <input
          type="checkbox"
          checked={tradeStyles[key]}
          onChange={() => {
            const newStyles = { ...tradeStyles, [key]: !tradeStyles[key] };
            setTradeStyles(newStyles);
            setTimeframes({
              ...timeframes,
              ...(key === "scalp" && { "5m": newStyles[key], "15m": newStyles[key] }),
              ...(key === "swing" && { "1h": newStyles[key], "6h": newStyles[key] }),
              ...(key === "day" && { "1d": newStyles[key] })
            });
          }}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span style={{
          position: "absolute",
          cursor: "pointer",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: tradeStyles[key] ? "#2563eb" : "#555",
          borderRadius: "26px",
          transition: ".4s"
        }}>
          <span style={{
            position: "absolute",
            height: "20px",
            width: "20px",
            left: tradeStyles[key] ? "26px" : "4px",
            bottom: "3px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            transition: ".4s"
          }} />
        </span>
      </label>
    </div>
  ))}
</div>

<iframe
  src={`https://s.tradingview.com/embed-widget/advanced-chart/?symbol=COINBASE:${selectedCoin.replace("-USD", "USD")}&interval=1&theme=dark`}
  width="100%"
  height="600"
  frameBorder="0"
  allowTransparency="true"
  scrolling="no"
  style={{ borderRadius: "10px", marginTop: "20px" }}
></iframe>                  

{/* Move Notifications BELOW the chart */}
<div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#1a1a1a", borderRadius: "10px", border: "1px solid #333" }}>
    <h3 style={{ color: "#58a6ff" }}>🔔 Recent Notifications</h3>
    {notificationLog.length === 0 ? (
        <p>No alerts yet.</p>
    ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {notificationLog.slice(0, 5).map((note, i) => (
                <div key={i} style={{ backgroundColor: "#2c2f33", padding: "10px", borderRadius: "8px", marginBottom: "10px" }}>
                    <p 
                        onClick={() => {
                            setSelectedCoin(note.coin);
                            setActiveTab("dashboard");
                        }} 
                        style={{ 
                            cursor: "pointer", 
                             
                            color: "#58a6ff" 
                        }}
                    >
                        {note.message}
                    </p>
                    <button
                        onClick={() => handleFollowTrade(note)}
                        style={{
                            marginTop: "8px",
                            backgroundColor: "#2563eb",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: "pointer"
                        }}
                    >
                        Follow Trade
                    </button>
                </div>
            ))}
        </div>
    )}
</div>


</>
                )}
                </main>
            </div>
        );
}

export default App; 